import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building } from "lucide-react";
import { useBusinesses } from "@/hooks/business/useBusinesses";
import { useBusinessData } from "@/hooks/business/useBusinessData";
import { BusinessDetails } from "@/types/index";

// Import the new tab components
import EmployeeListTab from "@/components/employees/EmployeeListTab";
import ShiftManagementTab from "@/components/shifts/ShiftManagementTab";
import TimeTrackingTab from "@/components/payroll/TimeTrackingTab";
import EmployeeInsightsTab from "@/components/employees/EmployeeInsightsTab";
import PayrollTab from "@/components/payroll/PayrollTab";
import GoogleCalendarIntegrationTab from "@/components/business/GoogleCalendarIntegrationTab";
import EventManagementTab from "@/components/events/EventManagementTab";
import UnifiedRecruitmentTab from "@/components/recruitment/UnifiedRecruitmentTab";
import { WhatsAppTab } from "@/components/whatsapp";
import { useDashboardTab } from "@/context/DashboardTabContext";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { employeeDashboardTab: activeView, setEmployeeDashboardTab: setActiveView } = useDashboardTab();

  const {
    businesses,
    setBusinesses,
    loadingBusinesses,
    selectedBusinessId,
    setSelectedBusinessId,
    updateBusinessInList,
  } = useBusinesses();

  // Conditional hook loading: use complex useBusinessData for Insights tab
  const needsComplexData = activeView === "insights";

  // Use useBusinessData only for Insights tab (needs full ExtendedBusinessDetails)
  const {
    selectedBusinessDetails: complexBusinessDetails,
    loadingData: complexLoadingData,
  } = useBusinessData(
    needsComplexData ? selectedBusinessId : null,
    needsComplexData ? businesses : [],
    0
  );

  // For non-Settings tabs, use basic business details from useBusinesses
  const selectedBusinessDetails = needsComplexData
    ? complexBusinessDetails
    : businesses?.find(b => b.id === selectedBusinessId) || null;

  const loadingData = needsComplexData ? complexLoadingData : loadingBusinesses;
  

  // Function to render employee content based on active view
  const renderEmployeeContent = (activeView: string, businessId: string | null, businessDetails: BusinessDetails | null, loadingData: boolean) => {
    switch (activeView) {
      case "insights":
        return <EmployeeInsightsTab setActiveView={setActiveView} businessDetails={businessDetails} />;
      case "employee-list":
        return <EmployeeListTab businessId={businessId} />;
      case "shift-management":
        return <ShiftManagementTab businessId={businessId} />;
      case "time-tracking":
        return <TimeTrackingTab business={businessDetails} loadingData={loadingData} />;
      case "events":
        return businessId && <EventManagementTab businessId={businessId} />;
      case "payroll":
        return (
          <PayrollTab
            business={businessDetails}
            loadingData={loadingData}
            onBusinessUpdate={(updates) => businessId && updateBusinessInList(businessId, updates)}
          />
        );
      case "job-postings":
        return businessId && <UnifiedRecruitmentTab businessId={businessId} />;
      case "google-calendar":
        return businessDetails && (
          <GoogleCalendarIntegrationTab 
            businessId={businessDetails.id}
            businessName={businessDetails.name}
            countryCode={businessDetails.country_code}
            shifts={[]} // Will need to be populated with actual shift data
            onShiftCreated={() => {}}
          />
        );
      case "communications":
        return <WhatsAppTab businessId={businessId} />;
      default:
        return <EmployeeInsightsTab setActiveView={setActiveView} businessDetails={businessDetails} />;
    }
  };

  // Note: loadingBusinesses is handled by SubscriptionRequiredRoute wrapper

  return (
    <>
      {!selectedBusinessId && businesses.length === 0 ? (
        <div className="mt-12 text-center bg-white dark:bg-card p-8 rounded-lg shadow">
          <Building className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" />
          <h2 className="mt-6 text-2xl font-semibold dark:text-gray-100">No Businesses Yet</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Create your first business to manage reviews, loyalty programs, and more.
          </p>
          <Button className="mt-6" onClick={() => navigate("/create-business")}>
            Create Your First Business
          </Button>
        </div>
      ) : !selectedBusinessId && businesses.length > 0 ? (
        <div className="mt-12 text-center bg-white dark:bg-card p-8 rounded-lg shadow">
          <Building className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500" />
          <h2 className="mt-6 text-2xl font-semibold dark:text-gray-100">Select a Business</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Please select a business from the dropdown above to view its dashboard.
          </p>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col min-h-0">
          {renderEmployeeContent(activeView, selectedBusinessId, selectedBusinessDetails, loadingData)}
        </div>
      )}
    </>
  );
};

export default EmployeeDashboard;
